import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { SignupJobSearchAgreementState } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";

import { WorkTypeOption } from "../InterestedWorkTypeStep/InterestedWorkTypeStepContent";

interface UseAgreementsToDisplayParams {
  interestedWorkType: WorkTypeOption | undefined;
  shouldAskInterestedWorkType: boolean;
}

export interface AgreementsToDisplay {
  showIcAgreement: boolean;
  showJobAgreement: boolean;
  isIcAgreementOptional: boolean;
  isJobAgreementOptional: boolean;
}

/**
 * Scenario 1: Interested Work Type page IS shown
 * User has explicitly selected their work type preference
 */
function getAgreementsWhenWorkTypePageShown(
  config: SignupJobSearchAgreementState,
  interestedWorkType: WorkTypeOption | undefined
): AgreementsToDisplay {
  const isFlexOnly = interestedWorkType === WorkTypeOption.FLEX;
  const isPermOnly = interestedWorkType === WorkTypeOption.PERMANENT_PLACEMENTS;
  const isBoth = interestedWorkType === WorkTypeOption.PERMANENT_AND_FLEX;

  // Added each possible condition for easier changes in the future
  switch (config) {
    case SignupJobSearchAgreementState.HIDDEN: {
      if (isFlexOnly) {
        // Flex only users only see IC Agreement (required)
        return {
          showIcAgreement: true,
          showJobAgreement: false,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      if (isPermOnly) {
        // Perm only users skip all agreements when HIDDEN
        return {
          showIcAgreement: false,
          showJobAgreement: false,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      if (isBoth) {
        // Both users only see IC Agreement (required)
        return {
          showIcAgreement: true,
          showJobAgreement: false,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      // Fallback
      return {
        showIcAgreement: true,
        showJobAgreement: false,
        isIcAgreementOptional: false,
        isJobAgreementOptional: false,
      };
    }

    case SignupJobSearchAgreementState.OPTIONAL: {
      if (isFlexOnly) {
        // Flex - show both agreements, IC is required, JA is optional
        return {
          showIcAgreement: true,
          showJobAgreement: true,
          isIcAgreementOptional: false,
          isJobAgreementOptional: true,
        };
      }
      if (isPermOnly) {
        // Perm only users only see Job Agreement (required)
        return {
          showIcAgreement: false,
          showJobAgreement: true,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      if (isBoth) {
        // Both agreements are required
        return {
          showIcAgreement: true,
          showJobAgreement: true,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      // Fallback
      return {
        showIcAgreement: true,
        showJobAgreement: true,
        isIcAgreementOptional: false,
        isJobAgreementOptional: true,
      };
    }

    case SignupJobSearchAgreementState.REQUIRED: {
      if (isFlexOnly) {
        // Flex - show both agreements (both required)
        return {
          showIcAgreement: true,
          showJobAgreement: true,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      if (isPermOnly) {
        // Perm only - shows only Job Agreement (required)
        return {
          showIcAgreement: true,
          showJobAgreement: true,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      if (isBoth) {
        // Both - show both agreements (both required)
        return {
          showIcAgreement: true,
          showJobAgreement: true,
          isIcAgreementOptional: false,
          isJobAgreementOptional: false,
        };
      }
      // Fallback
      return {
        showIcAgreement: true,
        showJobAgreement: true,
        isIcAgreementOptional: false,
        isJobAgreementOptional: false,
      };
    }

    default: {
      const exhaustiveCheck: never = config;
      throw new Error(`Unhandled config state: ${String(exhaustiveCheck)}`);
    }
  }
}

/**
 * Scenario 2: Interested Work Type page is NOT shown
 * User is implied to be a Flex worker
 */
function getAgreementsWhenWorkTypePageNotShown(
  config: SignupJobSearchAgreementState
): AgreementsToDisplay {
  switch (config) {
    case SignupJobSearchAgreementState.HIDDEN: {
      // Implied Flex users only see IC Agreement (required)
      return {
        showIcAgreement: true,
        showJobAgreement: false,
        isIcAgreementOptional: false,
        isJobAgreementOptional: false,
      };
    }

    case SignupJobSearchAgreementState.OPTIONAL: {
      // Show Job Agreement as optional IC remains required (interactive clickable links)
      return {
        showIcAgreement: true,
        showJobAgreement: true,
        isIcAgreementOptional: false,
        isJobAgreementOptional: true,
      };
    }

    case SignupJobSearchAgreementState.REQUIRED: {
      // Both agreements are required
      return {
        showIcAgreement: true,
        showJobAgreement: true,
        isIcAgreementOptional: false,
        isJobAgreementOptional: false,
      };
    }

    default: {
      const exhaustiveCheck: never = config;
      throw new Error(`Unhandled config state: ${String(exhaustiveCheck)}`);
    }
  }
}

export function useAgreementsToDisplay(params: UseAgreementsToDisplayParams): AgreementsToDisplay {
  const { interestedWorkType, shouldAskInterestedWorkType } = params;

  const signupJobSearchAgreementConfig: SignupJobSearchAgreementState = useCbhFlag(
    CbhFeatureFlag.SIGNUP_JOB_SEARCH_AGREEMENT_CONFIG,
    {
      defaultValue: SignupJobSearchAgreementState.REQUIRED,
    }
  );

  if (shouldAskInterestedWorkType) {
    return getAgreementsWhenWorkTypePageShown(signupJobSearchAgreementConfig, interestedWorkType);
  }

  return getAgreementsWhenWorkTypePageNotShown(signupJobSearchAgreementConfig);
}
